import { Table } from "antd";
import React, { FC, useMemo } from "react";
import { IJob } from "../helper";
import { useNavigate } from "react-router-dom";
import { EditablePriority } from "./editablePriority";

interface Props {
  list: Array<IJob>;
}

export const ProjectList: FC<Props> = ({ list }) => {
  const navigate = useNavigate();

  const dataSource = useMemo(
    () => list.map((job) => ({
      _id: job._id,
      companyName: job.companyName,
      positionTitle: job.positionTitle,
      completionPercentage: job.completionPercentage,
      totalApplicants: job.totalApplicants,
      priority: job.priority,
      managers: job.managers,
    })),
    [list]
  );

  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      align: "center" as const,
      render: (text: string, record: IJob) => (
        <a
          onClick={() => {
            if (record._id) navigate(`?job=${record._id}`);
          }}
          style={{ cursor: "pointer" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Position",
      dataIndex: "positionTitle",
      key: "positionTitle",
      align: "center" as const,
    },
   
    {
      title: "Total Applicants",
      key: "totalApplicants",
      align: "center" as const,
      render: (_: any, record: IJob) => record.totalApplicants ?? 0,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      align: "center" as const,
      render: (priority: number, record: IJob) => (
        <EditablePriority id={record._id} initialValue={priority ?? 0} />
      ),
    },
    {
      title: "Completion %",
      dataIndex: "completionPercentage",
      key: "completionPercentage",
      align: "center" as const,
      render: (val: number) => `${val ?? 0}%`,
    },
    {
      title: "Hiring Managers",
      dataIndex: "managers",
      key: "managers",
      align: "center" as const,
      render: (managers: any[]) =>
        Array.isArray(managers) && managers.length
          ? managers.map((m, idx) => (
              <span key={m.email || m._id}>
                {m.name}
                <span
                  style={{
                    marginLeft: 6,
                    color: m.isActive ? "green" : "orange",
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                >
                  {m.isActive ? " (Active)" : " (Pending)"}
                </span>
                {idx < managers.length - 1 ? ", " : ""}
              </span>
            ))
          : "-",
    },
  ];

  return (
    <Table 
      dataSource={dataSource} 
      columns={columns.map(col => ({
        ...col,
        title: <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{col.title}</span>,
        render: col.render ? (text: any, record: any) => {
          const rendered = col.render(text, record);
          if (React.isValidElement(rendered)) {
            return React.cloneElement(rendered as React.ReactElement<any>, {
              style: { ...(rendered as React.ReactElement<any>).props.style, fontSize: '16px' }
            });
          }
          return Array.isArray(rendered)
            ? <>{rendered}</>
            : <span style={{ fontSize: '16px' }}>{rendered}</span>;
        } : (text: any) => <span style={{ fontSize: '16px' }}>{text}</span>
      }))}
      rowKey="_id" 
      pagination={false}
      components={{
        body: {
          cell: (props: any) => (
            <td {...props} style={{ ...props.style, fontSize: '16px' }} />
          ),
        },
      }}
    />
  );
};

export default ProjectList;